import React from 'react';
import { ChevronLeftIcon } from '@heroicons/react/20/solid';
import { extractComponentFromChunk } from '@/ui/utils/tiles';
import Link from 'next/link';
import { useLocaleLink } from '@/ui/hooks/useLocaleLink';
import { buildTrackingId } from '@/utils/tracking-helper';
import { MobileSecondLevelMenuProps } from './types/MobileSecondLevelMenuProps';
import { resolveLinkUrlFromChunk } from '../../utils';
import { slugify } from '@/utils/url-helper';

export default function MobileSecondLevelMenu({
  menuData,
  setMenuData,
  close,
}: Readonly<MobileSecondLevelMenuProps>) {
  const { localizedLink } = useLocaleLink();
  const navItemTitle = menuData?.title;
  const backTrackingId = buildTrackingId('nav-mobile', navItemTitle, 'back');
  return (
    <li>
      <button
        type="button"
        onClick={() => setMenuData(null)}
        className="-ml-2 mb-10 flex items-center gap-1"
        data-tile-element-id={backTrackingId}
      >
        <ChevronLeftIcon className="h-8 w-auto" />
        {menuData?.title}
      </button>
      <ul className="flex flex-col gap-5">
        {menuData?.components.map((component) => {
          const components =
            component.content?.__typename === 'PieceContent' ? component.content.components : null;
          const links: {
            title: string;
            linkUrl: string;
          }[] = [];

          switch (component.id) {
            case 'header-text-component': {
              const title = extractComponentFromChunk(
                components,
                'title',
                'SingleLineContent',
              )?.text;

              const cta = extractComponentFromChunk(components, 'cta', 'PieceContent')?.components;
              const ctaUrl = resolveLinkUrlFromChunk(cta, localizedLink);
              if (title && ctaUrl) {
                links.push({ title, linkUrl: ctaUrl });
              }
              break;
            }
            case 'header-image-text-component': {
              const chunkLinks = extractComponentFromChunk(
                components,
                'links',
                'ContentChunkContent',
              )?.chunks;

              chunkLinks?.forEach((link) => {
                const linkTitle = extractComponentFromChunk(
                  link,
                  'title',
                  'SingleLineContent',
                )?.text;

                const linkUrl = resolveLinkUrlFromChunk(link, localizedLink);

                if (linkTitle && linkUrl) {
                  links.push({ title: linkTitle, linkUrl });
                }
              });
              break;
            }

            case 'header-link-list-component': {
              const linkList = extractComponentFromChunk(
                components,
                'links',
                'ContentChunkContent',
              )?.chunks;

              linkList?.forEach((link) => {
                const linkTitle = extractComponentFromChunk(
                  link,
                  'title',
                  'SingleLineContent',
                )?.text;
                const linkUrl = resolveLinkUrlFromChunk(link, localizedLink);

                if (linkTitle && linkUrl) {
                  links.push({ title: linkTitle, linkUrl });
                }
              });
              break;
            }
            default:
              break;
          }

          return links.map((link, idx) => {
            const navLinkTrackingId = buildTrackingId('nav-mobile', navItemTitle, link.title);

            return (
              <li key={`${slugify(link.linkUrl)}-${idx}`} className="text-2xl font-medium">
                <Link
                  onClick={() => close()}
                  href={link.linkUrl}
                  data-tile-element-id={navLinkTrackingId}
                >
                  {link.title}
                </Link>
              </li>
            );
          });
        })}
      </ul>
    </li>
  );
}
